/** Sidebar / party-tab progress step set (must match Content SidebarV2 + LOS V2). */
export const emptyCompletedSteps = {
    yourinfo: false,
    residence: false,
    property: false,
    employment: false,
    otherincome: false,
    assets: false,
    creditcheck: false,
    liabilities: false,
    propertymoney: false,
    finances: false,
    realestate: false,
}

const hasFilled = (value) => {
    if (value === null || value === undefined) return false
    if (typeof value === "number") return !Number.isNaN(value) && value !== 0
    if (typeof value === "boolean") return value
    return `${value}`.trim() !== ""
}

const hasAnyFilled = (...values) => values.some(hasFilled)

/**
 * Legacy / pre-V2 loans often have full field data but empty
 * borrower_profiles.completed_steps. Infer display-only progress from data.
 * Do not use this for resume / step unlock — only for progress % UI.
 */
export const inferCompletedStepsFromData = (application, person) => {
    if (!person) return { ...emptyCompletedSteps }
    const partyId = person.partyId
    const present = person.presentAddress || {}
    const occupation = person.occupation || {}
    const income = occupation.income || {}
    const otherIncome = person.income?.otherincome || person.income?.otherIncome || []
    const assets = (application?.finance?.assetsByPartyId || {})[partyId] || {}
    const liabilities = (application?.finance?.liabilitiesByPartyId || {})[partyId] || {}
    const realEstate = (application?.finance?.realEstateByPartyId || {})[partyId] || {}
    const property = application?.property || {}
    const declarations = (application?.declarationsByPartyId || {})[partyId] || {}

    const yourinfo = hasAnyFilled(
        person.firstName,
        person.lastName,
        person.socialSecurity,
        person.dateOfBirth,
        person.email,
        person.citizenship,
        person.maritalStatus,
        person.homePhone,
        person.mobilePhone,
    )
    const residence = hasAnyFilled(
        present.street,
        present.address,
        person.residenceOwnership,
        present.years,
        present.months,
    )
    const propertyDone = hasAnyFilled(
        property.purpose,
        property.address,
        property.street,
        property.appraisal,
        property.salesprice,
        property.occupancy,
    )
    const employment = hasAnyFilled(
        occupation.employername,
        occupation.employerstreet,
        occupation.employeraddress,
        occupation.hasoccupation,
        occupation.positiontitle,
        income.base,
        income.basemonthly,
        ...(person.otheroccupations || []).map((item) => item?.employername),
        ...(person.previousoccupations || []).map((item) => item?.employername),
    )
    const hasOtherIncomeRows = Array.isArray(otherIncome) && otherIncome.some((item) => (
        hasAnyFilled(item?.amount, item?.incometype, item?.description)
    ))
    const otherincome = hasOtherIncomeRows || employment
    const accounts = assets.accounts || []
    const otherAssets = assets.otherAssets || []
    const assetsDone = (
        (Array.isArray(accounts) && accounts.some((item) => hasAnyFilled(item?.bankName, item?.balance, item?.account)))
        || (Array.isArray(otherAssets) && otherAssets.some((item) => hasAnyFilled(item?.assetType, item?.cashOrMarketValue, item?.balance)))
    )
    const loanList = liabilities.loans || []
    const expenses = liabilities.additionalExpenses || person.income?.alimony || []
    const liabilitiesDone = (
        (Array.isArray(loanList) && loanList.some((item) => hasAnyFilled(
            item?.name,
            item?.monthlyPayment,
            item?.monthlypayment,
            item?.remainingBalance,
            item?.remainingbalance,
        )))
        || (Array.isArray(expenses) && expenses.some((item) => hasAnyFilled(item?.amount, item?.expenseType, item?.expensetype)))
    )
    const creditcheck = person.creditconsented === true || hasFilled(person.estimatedFico)
    const reoList = realEstate.properties || []
    const realestate = (
        `${person.residenceOwnership || ""}`.toLowerCase() === "own"
        || (Array.isArray(reoList) && reoList.some((item) => hasAnyFilled(item?.street, item?.address, item?.marketValue)))
    )
    const propertymoney = hasAnyFilled(
        declarations.occupyasprimary,
        declarations.ownershipinterest,
        declarations.downpaymentborrowed,
        declarations.moneyborrowed,
    )
    const finances = hasAnyFilled(
        declarations.judgements,
        declarations.bancrupt,
        declarations.propertyforeclosure,
        declarations.lawsuit,
        declarations.delinquentondebt,
        declarations.conveyedtitle,
        declarations.comakeronnote,
        declarations.servearmedforces,
        declarations.loansinforebearance,
    )

    return {
        yourinfo,
        residence,
        property: propertyDone,
        employment,
        otherincome,
        assets: assetsDone,
        creditcheck,
        liabilities: liabilitiesDone,
        propertymoney,
        finances,
        realestate,
    }
}

/** Prefer stored completedSteps when any flag is true; else infer from field data. */
export const effectiveCompletedStepsForDisplay = (application, person) => {
    const stored = person?.completedSteps || {}
    const hasStoredProgress = Object.values(stored).some(Boolean)
    if (hasStoredProgress) {
        return { ...emptyCompletedSteps, ...stored }
    }
    return inferCompletedStepsFromData(application, person)
}

export const participantCompletionPercent = (person, application) => {
    const steps = effectiveCompletedStepsForDisplay(application, person)
    const values = Object.values(steps)
    if (!values.length) return 0
    const done = values.filter(Boolean).length
    return Math.round((done / values.length) * 100)
}
